import React, { useMemo } from 'react';
import RequestItem from './RequestItem';
import ExpandableSection from '../ui/ExpandableSection';
import { ShoppingCartIcon } from '../common/IconsComponent';

/* ─── Empty State ──────────────────────────────────────────────────────────── */

const EmptyState = () => (
  <div className="relative overflow-hidden rounded-xl border border-gray-100 bg-white">
    <div
      aria-hidden
      className="pointer-events-none absolute -top-8 -right-8 h-32 w-32 rounded-full bg-gradient-to-br from-indigo-100 to-blue-100 blur-2xl opacity-60"
    />
    <div
      aria-hidden
      className="pointer-events-none absolute -bottom-10 -left-10 h-36 w-36 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 blur-2xl opacity-60"
    />

    <div className="relative text-center px-6 py-8 sm:py-10">
      <span className="inline-flex h-11 w-11 sm:h-12 sm:w-12 items-center justify-center rounded-full bg-indigo-50 text-indigo-500 ring-1 ring-indigo-100">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5 sm:h-6 sm:w-6"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <line x1="16" y1="2.5" x2="16" y2="6" />
          <line x1="8" y1="2.5" x2="8" y2="6" />
          <line x1="3" y1="9" x2="21" y2="9" />
          <line x1="12" y1="13" x2="12" y2="19" />
          <line x1="9" y1="16" x2="15" y2="16" />
        </svg>
      </span>

      <h3 className="mt-3 text-sm sm:text-base font-semibold text-gray-900">
        כרגע אין בקשות לשיעורים.
      </h3>
      <p className="mt-1 text-xs sm:text-sm text-gray-500">
        צרו בקשת שיעור חדשה כדי להתחיל.
      </p>
    </div>
  </div>
);

/* ─── Helpers ──────────────────────────────────────────────────────────────── */

const normalizeStatus = (status) => {
  const s = String(status || '').trim().toUpperCase();

  if (s === 'PENDING') return 'NO_RESPONSE';
  if (s === 'REJECTED') return 'NOT_APPROVED';
  if (s === 'APPROVED') return 'APPROVED';
  if (s === 'NO_RESPONSE') return 'NO_RESPONSE';
  if (s === 'NOT_APPROVED') return 'NOT_APPROVED';
  if (s === 'PAID') return 'PAID';

  return s;
};

const isPaidRequest = (request) => {
  return (
    request?.paid === true ||
    request?.isPaid === true ||
    request?.purchased === true ||
    String(request?.paymentStatus || '').toUpperCase() === 'PAID' ||
    normalizeStatus(request?.status) === 'PAID'
  );
};

const toDateTime = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;

  const safeTime = String(timeStr).slice(0, 5);
  const [Y, M, D] = String(dateStr).split('-').map(Number);
  const [h, m] = safeTime.split(':').map(Number);

  const d = new Date(Y, (M || 1) - 1, D || 1, h || 0, m || 0, 0, 0);
  return Number.isNaN(d.getTime()) ? null : d;
};

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const getApprovedAt = (request) =>
  toDate(
    request?.approvedAt ||
      request?.approved_at ||
      request?.statusUpdatedAt ||
      request?.status_updated_at ||
      request?.updatedAt ||
      request?.updated_at
  );

const getPaymentDeadline = (request) => {
  const lessonStart = toDateTime(
    request?.date,
    request?.startTime || request?.start_time
  );

  const explicitDeadline = toDate(
    request?.paymentDeadlineAt || request?.payment_deadline_at
  );
  if (explicitDeadline) return explicitDeadline;

  const approvedAt = getApprovedAt(request);
  if (!approvedAt || !lessonStart) return null;

  const fiveHoursAfterApproval = new Date(
    approvedAt.getTime() + 5 * 60 * 60 * 1000
  );
  const oneHourBeforeLesson = new Date(
    lessonStart.getTime() - 60 * 60 * 1000
  );

  return new Date(
    Math.min(fiveHoursAfterApproval.getTime(), oneHourBeforeLesson.getTime())
  );
};

const shouldShowRequest = (request, nowMs) => {
  const status = normalizeStatus(request?.status);

  if (isPaidRequest(request)) return true;
  if (status === 'NOT_APPROVED') return true;

  if (status === 'APPROVED') return true;

  if (status === 'NO_RESPONSE') {
    const lessonStart = toDateTime(
      request?.date,
      request?.startTime || request?.start_time
    );

    if (!lessonStart) return true;

    const expiryTime = lessonStart.getTime() - 60 * 60 * 1000;
    return nowMs < expiryTime || nowMs >= expiryTime;
  }

  return false;
};

/* ─── Component ────────────────────────────────────────────────────────────── */

const RequestsList = ({
  lessonRequests = [],
  selectedRequests = [],
  onRemove,
  serverNowMs,
}) => {
  const effectiveNowMs = Number.isFinite(serverNowMs) ? serverNowMs : Date.now();

  const normalizedRequests = useMemo(() => {
    const source = Array.isArray(lessonRequests) ? lessonRequests : [];

    return source.map((request) => {
      const normalizedStatus = normalizeStatus(request?.status);
      const paid = isPaidRequest(request);
      const paymentDeadline = getPaymentDeadline(request);

      return {
        ...request,
        status: paid ? 'PAID' : normalizedStatus,
        paymentDeadlineAt:
          request?.paymentDeadlineAt ||
          request?.payment_deadline_at ||
          paymentDeadline?.toISOString() ||
          null,
      };
    });
  }, [lessonRequests]);

  const visible = useMemo(() => {
    return normalizedRequests.filter((request) =>
      shouldShowRequest(request, effectiveNowMs)
    );
  }, [normalizedRequests, effectiveNowMs]);

  return (
    <ExpandableSection
      title={`בקשות לשיעורים פרטיים (${visible.length})`}
      icon={<ShoppingCartIcon />}
      headerClassName="bg-gradient-to-l from-indigo-50 to-blue-50"
    >
      <div
        className="px-4 pt-3 pb-4 sm:px-5 sm:pt-4 sm:pb-5 space-y-2.5 sm:space-y-3"
        dir="rtl"
      >
        {visible.length === 0 ? (
          <EmptyState />
        ) : (
          visible.map((request) => (
            <RequestItem
              key={request.id}
              request={request}
              onRemove={onRemove}
              serverNowMs={serverNowMs}
              isSelected={
                Array.isArray(selectedRequests) &&
                selectedRequests.some((r) => String(r?.id) === String(request?.id))
              }
            />
          ))
        )}
      </div>
    </ExpandableSection>
  );
};

export default RequestsList;